<div class="min-vh-100 bg-light">
  <app-header [page]="'Admin'"></app-header>

  <main class="container py-4">
    <div class="mb-4">
      <h4 class="mb-1 fw-bold">Withdrawal Setup Guide</h4>
      <div class="text-muted small">
        Step-by-step instructions for configuring withdrawal gateways and dynamic payout fields
      </div>
    </div>

    <div class="card shadow-sm border-0 mb-4">
      <div class="card-body">
        <div class="d-flex flex-wrap gap-2">
          <button class="btn btn-primary btn-sm" type="button" (click)="goTo('/admin/settings')">
            Go to Settings
          </button>
          <button class="btn btn-outline-primary btn-sm" type="button" (click)="goTo('/admin/withdrawals/gateways')">
            Go to Withdrawal Gateways
          </button>
          <button class="btn btn-outline-primary btn-sm" type="button" (click)="goTo('/admin/withdrawals/fields')">
            Go to Withdrawal Fields
          </button>
        </div>
      </div>
    </div>

    <div class="card shadow-sm border-0 mb-4">
      <div class="card-body">
        <h5 class="fw-bold mb-3">Overview</h5>
        <p class="mb-2">
          Use this guide when setting up a new withdrawal route, especially Nigerian Flutterwave bank transfer.
        </p>
        <p class="mb-0">
          The recommended order is:
          <strong>Provider → Gateway Config → Withdrawal Gateway Mapping → Sync Institutions → Withdrawal Fields → Test User Flow</strong>.
        </p>
      </div>
    </div>

    <div class="card shadow-sm border-0 mb-4">
      <div class="card-body">
        <h5 class="fw-bold mb-3">Step 1 — Create or confirm the payment provider</h5>
        <p class="mb-0">
          Go to <strong>Administrator → Payment Gateways → Payment Providers</strong> and confirm that the provider exists,
          for example <code>FLUTTERWAVE</code>.
        </p>
      </div>
    </div>

    <div class="card shadow-sm border-0 mb-4">
      <div class="card-body">
        <h5 class="fw-bold mb-3">Step 2 — Create or confirm the gateway config</h5>
        <p class="mb-2">
          Go to <strong>Administrator → Payment Gateways → Gateway Configs</strong>.
        </p>
        <ul class="mb-0">
          <li>Provider: <code>FLUTTERWAVE</code></li>
          <li>Country Code: <code>NG</code></li>
          <li>Environment: <code>Test</code> or <code>Live</code></li>
          <li>Secret Key and Base URL must be valid</li>
        </ul>
      </div>
    </div>

    <div class="card shadow-sm border-0 mb-4">
      <div class="card-body">
        <h5 class="fw-bold mb-3">Step 3 — Create the withdrawal gateway mapping</h5>
        <p class="mb-2">
          Go to <strong>Administrator → Withdrawal Gateways</strong> and create the routing rule for the withdrawal method.
        </p>

        <div class="d-flex justify-content-between align-items-center mb-2">
          <div class="fw-semibold">Recommended NGN example</div>
          <button class="btn btn-outline-secondary btn-sm" type="button" (click)="copyText(gatewayMappingExample, 'Gateway mapping example')">
            Copy JSON
          </button>
        </div>
        <pre class="json-box mb-0"><code>{{ gatewayMappingExample }}</code></pre>
      </div>
    </div>

    <div class="card shadow-sm border-0 mb-4">
      <div class="card-body">
        <h5 class="fw-bold mb-3">Step 4 — Sync Nigerian banks</h5>
        <p class="mb-0">
          Go to <strong>Administrator → Settings</strong> and click <strong>Sync Nigerian Banks</strong>.
          This loads the bank list into the global lookup table so the bank dropdown can be populated from your database instead of relying on a live provider call.
        </p>
      </div>
    </div>

    <div class="card shadow-sm border-0 mb-4">
      <div class="card-body">
        <h5 class="fw-bold mb-3">Step 5 — Create the withdrawal payout fields</h5>
        <p class="mb-3">
          Go to <strong>Administrator → Withdrawal Fields</strong> and create the destination fields in the correct sort order.
        </p>

        <div class="table-responsive">
          <table class="table align-middle">
            <thead>
              <tr>
                <th>Field Key</th>
                <th>Label</th>
                <th>Type</th>
                <th>Required</th>
                <th>Sort Order</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>bankCode</code></td>
                <td>Bank</td>
                <td>select</td>
                <td>Yes</td>
                <td>1</td>
                <td>Uses synced Nigerian institutions</td>
              </tr>
              <tr>
                <td><code>accountNumber</code></td>
                <td>Account Number</td>
                <td>text</td>
                <td>Yes</td>
                <td>2</td>
                <td>Must be 10 digits and triggers account name resolution</td>
              </tr>
              <tr>
                <td><code>accountName</code></td>
                <td>Account Name</td>
                <td>text</td>
                <td>Yes</td>
                <td>3</td>
                <td>Readonly resolved field</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="card shadow-sm border-0 mb-4">
      <div class="card-body">
        <h5 class="fw-bold mb-3">JSON Templates</h5>

        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="fw-semibold">Bank select — Options JSON</div>
            <button class="btn btn-outline-secondary btn-sm" type="button" (click)="copyText(bankOptionsJson, 'Bank select Options JSON')">
              Copy JSON
            </button>
          </div>
          <pre class="json-box"><code>{{ bankOptionsJson }}</code></pre>
        </div>

        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="fw-semibold">Account number — Validation JSON</div>
            <button class="btn btn-outline-secondary btn-sm" type="button" (click)="copyText(accountNumberValidationJson, 'Account number Validation JSON')">
              Copy JSON
            </button>
          </div>
          <pre class="json-box"><code>{{ accountNumberValidationJson }}</code></pre>
        </div>

        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="fw-semibold">Account number — Metadata JSON</div>
            <button class="btn btn-outline-secondary btn-sm" type="button" (click)="copyText(accountNumberMetadataJson, 'Account number Metadata JSON')">
              Copy JSON
            </button>
          </div>
          <pre class="json-box"><code>{{ accountNumberMetadataJson }}</code></pre>
        </div>

        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="fw-semibold">Account name — Metadata JSON</div>
            <button class="btn btn-outline-secondary btn-sm" type="button" (click)="copyText(resolvedAccountNameMetadataJson, 'Account name Metadata JSON')">
              Copy JSON
            </button>
          </div>
          <pre class="json-box"><code>{{ resolvedAccountNameMetadataJson }}</code></pre>
        </div>

        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="fw-semibold">Static select example — Options JSON</div>
            <button class="btn btn-outline-secondary btn-sm" type="button" (click)="copyText(staticSelectJson, 'Static select Options JSON')">
              Copy JSON
            </button>
          </div>
          <pre class="json-box"><code>{{ staticSelectJson }}</code></pre>
        </div>

        <div class="mb-0">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="fw-semibold">Email validation example — Validation JSON</div>
            <button class="btn btn-outline-secondary btn-sm" type="button" (click)="copyText(emailValidationJson, 'Email Validation JSON')">
              Copy JSON
            </button>
          </div>
          <pre class="json-box"><code>{{ emailValidationJson }}</code></pre>
        </div>
      </div>
    </div>

    <div class="card shadow-sm border-0 mb-4">
      <div class="card-body">
        <h5 class="fw-bold mb-3">Common mistakes</h5>
        <ul class="mb-0">
          <li>Forgetting to sync banks before testing the bank dropdown</li>
          <li>Using the wrong provider code in <code>OptionsJson</code></li>
          <li>Creating fields with the wrong payment method</li>
          <li>Forgetting the resolved <code>accountName</code> field</li>
          <li>Saving invalid JSON in Options, Validation, or Metadata</li>
          <li>Creating gateway mappings that are inactive or not set as default when expected</li>
        </ul>
      </div>
    </div>

    <div class="card shadow-sm border-0 mb-4">
      <div class="card-body">
        <h5 class="fw-bold mb-3">Testing checklist</h5>
        <div class="row g-3">
          <div class="col-md-6">
            <div class="check-item">Provider exists and is active</div>
            <div class="check-item">Gateway config exists and is active</div>
            <div class="check-item">Withdrawal mapping exists and is active</div>
          </div>
          <div class="col-md-6">
            <div class="check-item">Banks synced successfully</div>
            <div class="check-item">Resolve account returns account name</div>
            <div class="check-item">User can complete confirm and initiate flow</div>
          </div>
        </div>
      </div>
    </div>

    <app-footer></app-footer>
  </main>
</div>